import { useTranslation } from 'react-i18next'
import { StyleSheet, ViewProps } from 'react-native'
import { AccessType, Workspace, useZone, useWorkspaces } from 'zens-store'
import { Button, PopUp, Spacing } from 'zens-storybook'
import { Suspense, useState } from 'react'

import { usePermissions } from '@dms/hook'

import { WorkspaceTable, WorkspaceTableProps } from '../workspace'
import { Panel } from '../layout'

import ZoneAddWorkspacesTable from './addworkspacestable'

import { ZoneWorkspaceEmpty } from '.'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  popup: {
    padding: Spacing.M,
    minWidth: 900,
  },
})

interface Props extends Omit<WorkspaceTableProps, 'onAdd' | 'onRemove'>, ViewProps {
  showAddWorkspaces?: boolean
  onAdd?: (workspaces: Workspace[]) => Promise<void>
  onRemove?: (workspace: Workspace) => Promise<void>
  viewType?: 'table' | 'floorplan'
}

const ZoneWorkspaceTable = (props: Props) => {
  const {
    companyId,
    zoneId = 0,
    pagination,
    onPaginationChange,
    sort = { field: 'name', direction: 'asc' },
    onSortChange,
    showAddWorkspaces,
    onRemove,
    onAdd,
    style,
    ...otherProps
  } = props
  const [addWorkspacesVisible, setAddWorkspacesVisible] = useState(!!showAddWorkspaces)
  const { t } = useTranslation()
  const workspaces = useWorkspaces({ companyId, filter: { zoneId: [zoneId] }, pagination, sort })
  const zone = useZone({ companyId, zoneId })
  const { hasPermission } = usePermissions(companyId)

  const addWorkspaces = async (data: Workspace[]) => {
    await onAdd?.(data)
    workspaces.refetch()
    hideAddZones()
  }

  const removeWorkspace = async (data: Workspace) => {
    await onRemove?.(data)
    workspaces.refetch()
  }

  const showAddZones = () => {
    setAddWorkspacesVisible(true)
  }
  const hideAddZones = () => {
    setAddWorkspacesVisible(false)
  }
  return (
    <>
      <Panel
        style={[styles.container, style]}
        title={`${t('Workspaces')} (${workspaces.data?.meta?.total || 0})`}
        right={
          (hasPermission(AccessType.Update, 'workspace') && workspaces.data?.data?.length && (
            <Button title={t('Add workspaces')} left="plus" onPress={showAddZones} testID="WorkspaceList.Add" />
          )) ||
          undefined
        }
        {...otherProps}
      >
        <WorkspaceTable
          companyId={companyId}
          zoneId={zoneId}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          sort={sort}
          onSortChange={onSortChange}
          filter={{ zoneId: [zoneId] }}
          onRemove={removeWorkspace}
          ListEmptyComponent={<ZoneWorkspaceEmpty onAdd={showAddZones} companyId={companyId} />}
          typeHref={(type) =>
            type === 'table'
              ? `/companies/${companyId}/workspaces/zones/${zoneId}`
              : `/companies/${companyId}/workspaces/zones/${zoneId}/floorplan`
          }
        />
      </Panel>
      <PopUp visible={addWorkspacesVisible} onClose={hideAddZones} style={styles.popup} closeButton={false}>
        <Suspense fallback={<ZoneAddWorkspacesTable.Skeleton />}>
          <ZoneAddWorkspacesTable
            zone={zone.data}
            companyId={companyId}
            onSave={addWorkspaces}
            onCancel={hideAddZones}
          />
        </Suspense>
      </PopUp>
    </>
  )
}

interface SkeletonProps extends ViewProps {
  companyId: number
  zoneId?: number
}

const Skeleton = (props: SkeletonProps) => {
  const { companyId, zoneId, style, ...otherProps } = props
  const { t } = useTranslation()
  const { hasPermission } = usePermissions(companyId)
  return (
    <Panel.Skeleton
      style={[styles.container, style]}
      title={t('Workspaces')}
      right={hasPermission(AccessType.Update, 'workspace') && <Button.Skeleton />}
      {...otherProps}
    >
      <WorkspaceTable.Skeleton zoneId={zoneId} />
    </Panel.Skeleton>
  )
}
ZoneWorkspaceTable.Skeleton = Skeleton

export default ZoneWorkspaceTable
